import { computed } from 'vue';

import { canCreateTenantsByRole, canManageTenantsByRole } from '@/domains/permissions/role/tenants';
import { canBrowseEmployeesByRole } from '@/domains/permissions/role/employees';
import { concatFullname } from '@/domains/concatFullname';
import { joinString } from '@/utils';
import { TTenantEmployeeRole } from '@/types';
import { useUserStore } from '@/stores/user';
import { useTenantsStore } from '@/stores/tenants';
import { hasBaseCarrierWorkplaceSubscription, hasCarrierWorkplaceSubscription } from '@/domains/permissions/subscription';
import { canBrowseWalletsByRole } from '@/domains/permissions/role/wallets';
import { useAppStore } from '@/stores/app';
import { EWasAuthBy } from '@/constants';
import { checkFeatureIsEnabled } from '@/domains/checkFeatureIsEnabled';
import { EExperimentalFeatures } from '@/domains/constants';

export const useView = () => {
  const appStore = useAppStore();
  const userStore = useUserStore();
  const tenantsStore = useTenantsStore();
  const currentTenant = computed(() => tenantsStore.currentTenant);
  const availableUserTenants = computed(() => tenantsStore.userTenants || []);
  const isAuthByTemporaryAccessSession = computed<boolean>(
    () => appStore.wasAuthBy === EWasAuthBy.temporaryAccessSession);
  const fullName = computed(() => concatFullname(currentTenant.value?.employee));
  const email = computed(() => userStore.user?.email || '');
  const showOrganizations = computed(() => !userStore.user?.ownerTenantId && !isAuthByTemporaryAccessSession.value);
  const showSettingsButton = computed(() => canManageTenantsByRole() && currentTenant.value?.employee.id);
  const employeeRoles = computed(() => joinString(currentTenant.value?.employee.roles.map((role: TTenantEmployeeRole) => role.name), ', '));
  const showEmployeeProfileLink = computed(() => currentTenant.value?.employee.id && canBrowseEmployeesByRole());
  const isAddTenantButtonShown = computed(() => canCreateTenantsByRole());

  const isRefactoringAddTenantFormAvailable = computed(
    () => checkFeatureIsEnabled(EExperimentalFeatures.refactoringAddTenantForm),
  );

  const getHasOpenClosedBiddingAdditionalSubscription = computed(
    () => tenantsStore.getHasOpenClosedBiddingAdditionalSubscription,
  );

  const isBillingWalletLinkShown = computed(
    () => (hasCarrierWorkplaceSubscription() || hasBaseCarrierWorkplaceSubscription())
      && canBrowseWalletsByRole()
      && getHasOpenClosedBiddingAdditionalSubscription.value,
  );

  return {
    isAddTenantButtonShown,
    currentTenant,
    availableUserTenants,
    fullName,
    email,
    showOrganizations,
    showSettingsButton,
    employeeRoles,
    showEmployeeProfileLink,
    isBillingWalletLinkShown,
    isAuthByTemporaryAccessSession,
    isRefactoringAddTenantFormAvailable,
  };
};
